import {css} from '@emotion/react'

import {PricingResponse} from '../api/usePricing'
import {ProductMetadataResponse} from '../api/fetchProductMetadata'
import unitsToReadableString from '../utils/unitsToReadableString'
import priceToString from '../utils/priceToString'
import useUsage from '../api/useUsage'

import Skeleton from './Skeleton'

const lineItemCss = css`
  font-size: 14px;
  line-height: 20px;
`

const boldCss = css`
  font-weight: bold;
`

const skeletonCss = css`
  height: 18px;
  margin: 12px 0;
`

interface UsageTableSummaryProps {
  price?: PricingResponse
  productMeta: ProductMetadataResponse
  usageDateRange: {
    startTimestamp: string
    endTimestamp: string
  }
}

function getPriceForUnits(units: number, price: PricingResponse): string {
  switch (price.stripeInfo.usageType) {
    case 'licensed': {
      return priceToString(price.stripeInfo.amount)
    }
    case 'metered': {
      return priceToString(price.stripeInfo.amount * units)
    }
    default: {
      return 'Unknown'
    }
  }
}

function getRandomWidth(): number {
  return 200 + Math.random() * 100
}

export default function UsageTableSummary({
  price,
  productMeta,
  usageDateRange,
}: UsageTableSummaryProps): JSX.Element {
  const usageRecordsState = useUsage({
    stripePriceId: price?.stripePriceId,
    startTimestamp: usageDateRange.startTimestamp,
    endTimestamp: usageDateRange.endTimestamp,
    charged: true,
  })

  if (usageRecordsState.status === 'error') throw usageRecordsState.error

  if (usageRecordsState.status === 'pending') {
    return (
      <>
        <Skeleton css={skeletonCss} width={`${getRandomWidth()}px`} />
        <Skeleton css={skeletonCss} width={`${getRandomWidth()}px`} />
        <Skeleton css={skeletonCss} width={`${getRandomWidth()}px`} />
      </>
    )
  }

  return (
    <>
      <p css={lineItemCss}>
        <span css={boldCss}>API Usage for:</span> Current Billing Cycle
      </p>
      <p css={lineItemCss}>
        <span css={boldCss}>Total {productMeta.unitDescription} in Period: </span>
        {unitsToReadableString(usageRecordsState.value.amountSum, productMeta.unitType)}
      </p>
      {price && (
        <p css={lineItemCss}>
          <span css={boldCss}>Estimated Charges for Period: </span>
          {getPriceForUnits(usageRecordsState.value.amountSum, price)}
        </p>
      )}
    </>
  )
}
